/**
 * "Demo link": build the variant on screen and get back a URL to send someone.
 *
 * The snapshot editor's Share link (snapshotEditor/dialogShareLink.js) in pe3d
 * chrome, and deliberately the same thing to use: two links come back and they
 * are shown ONE AT A TIME behind a switch rather than stacked —
 *
 *   demo   the site's viewer — device frame, rotate, QR code
 *   raw    the playable itself, for an ad network's preview tool or an iframe
 *
 * Nothing is opened automatically. A build takes minutes and a `window.open`
 * that far from the click is what pop-up blockers exist to stop, which is how
 * this used to lose the link altogether.
 *
 * The loading screen is not asked for here: the game's own is used, as the
 * export's is (server makeCreatives).
 */

import React, { useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { BAR_BG, CONTROL_BG, CONTROL_BORDER, FIELD_LABEL } from "../snapshotEditor/panelPalette";
import { previewVariantLink } from "../../modules/pe3dApi";
import { DIALOG_PAPER_PROPS, DIALOG_TEXT, MUTED, TEXT } from "./pe3dPanelKit";

const ACCENT = "#4eabb1";

const LINK_VIEWS = [
  { id: "demo", label: "Demo", caption: "Opens the viewer — device frame, rotate, QR code." },
  { id: "raw", label: "Raw", caption: "The playable itself, for a preview tool or an <iframe>." },
];

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", gap: "12px", minWidth: 0 },
  title: { flex: 1, minWidth: 0, fontSize: "13px", color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  option: { display: "flex", alignItems: "center", gap: "8px", marginTop: "12px" },
  optionLabel: { fontSize: "11px", color: FIELD_LABEL, textTransform: "uppercase", letterSpacing: "0.04em" },
  optionSelect: { fontSize: "12px", color: "#fff", backgroundColor: CONTROL_BG, border: `1px solid ${CONTROL_BORDER}`, borderRadius: 4, padding: "0 6px" },
  switch: { display: "flex", gap: "2px", marginTop: "18px", backgroundColor: BAR_BG, borderRadius: 4, padding: "2px", width: "fit-content" },
  switchButton: { fontSize: "12px", minWidth: "72px", padding: "2px 10px", textTransform: "none", color: MUTED, borderRadius: 3 },
  switchOn: { backgroundColor: CONTROL_BG, color: "#fff" },
  caption: { fontSize: "11px", color: MUTED, marginTop: "8px" },
  linkRow: { display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", backgroundColor: CONTROL_BG, border: `1px solid ${CONTROL_BORDER}`, borderRadius: 4, padding: "6px 8px" },
  link: { flex: 1, minWidth: 0, fontSize: "12px", color: ACCENT, wordBreak: "break-all" },
});

export default function DialogPe3dShareLink({ open, onClose, editor, selectedBase, createSnackbar, onLinkMade }) {
  const classes = useStyles();
  const [isBuilding, setIsBuilding] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [language, setLanguage] = useState(null);
  const [linkView, setLinkView] = useState("demo");
  const [copied, setCopied] = useState(false);

  const variant = editor.doc;
  const title = (variant && variant.title) || "No variant selected";
  const languages = (editor.languages && editor.languages.length ? editor.languages : ["en"]);
  const chosenLanguage = language && languages.includes(language) ? language : languages[0];
  const view = LINK_VIEWS.find((item) => item.id === linkView) || LINK_VIEWS[0];
  const url = result ? result[view.id] : null;

  const reset = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const run = async () => {
    if (!variant) return;
    if (editor.saveState !== "saved" && !(await editor.save())) {
      createSnackbar("The variant has problems the server would refuse — fix them, then make the link.", "warning", false);
      return;
    }
    setIsBuilding(true);
    reset();
    setProgress("Starting…");
    const answer = await previewVariantLink(
      { baseID: selectedBase._id, variantID: variant._id, language: chosenLanguage },
      { onProgress: (events) => setProgress(_.get(events[events.length - 1], "stage", "Working…")) }
    );
    setIsBuilding(false);
    setProgress("");

    if (!answer.ok || !_.get(answer, "data.file")) {
      const detail = typeof answer.error === "string" ? answer.error : JSON.stringify(answer.error);
      setError(detail);
      createSnackbar(`Could not make the demo link — ${detail}`, "error", false);
      return;
    }

    const { file, previewData } = answer.data;
    const demo = `https://demo.playin.com.tr/?file=${previewData.timeStamp}_${previewData.language}_${previewData.fileName.replace(/\.html$/, "")}&dashboard=true`;
    setResult({ demo, raw: file.Location });
    setLinkView("demo");
    createSnackbar("Demo link ready.", "success", false);
    // The plan counter and the preview record are the caller's, as before.
    if (onLinkMade) onLinkMade({ file, previewData, demoURL: demo });
  };

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (_err) {
      createSnackbar("Could not copy — select the link and copy it by hand.", "warning", false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={isBuilding ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER_PROPS}>
      <DialogTitle>Demo link</DialogTitle>
      <DialogContent>
        <div className={classes.head}>
          <Typography className={classes.title} title={title}>
            {title}
          </Typography>
        </div>

        {languages.length > 1 && (
          <div className={classes.option}>
            <span className={classes.optionLabel}>Language</span>
            <Select
              value={chosenLanguage}
              disabled={isBuilding}
              disableUnderline
              className={classes.optionSelect}
              onChange={(event) => {
                setLanguage(event.target.value);
                // Each language is its own build, so the link on screen stops
                // belonging to what is selected.
                reset();
              }}
            >
              {languages.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        {isBuilding && (
          <div style={{ ...DIALOG_TEXT.busy, marginTop: 18 }}>
            <CircularProgress size={18} />
            <span>
              Building the playable…
              {progress && <span style={DIALOG_TEXT.waitLine}>{progress}</span>}
            </span>
          </div>
        )}

        {error && <Typography style={DIALOG_TEXT.failure}>{error}</Typography>}

        {result && (
          <>
            <div className={classes.switch}>
              {LINK_VIEWS.map((item) => (
                <Button
                  key={item.id}
                  className={`${classes.switchButton} ${linkView === item.id ? classes.switchOn : ""}`}
                  onClick={() => {
                    setLinkView(item.id);
                    setCopied(false);
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <Typography className={classes.caption}>{view.caption}</Typography>
            <div className={classes.linkRow}>
              <a className={classes.link} href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </a>
              <Tooltip title={copied ? "Copied" : "Copy the link"}>
                <Button size="small" onClick={copyLink}>
                  {copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon fontSize="small" />}
                </Button>
              </Tooltip>
              <Tooltip title="Open in a new tab">
                <Button size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon fontSize="small" />
                </Button>
              </Tooltip>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBuilding}>
          Close
        </Button>
        <Button color="primary" variant="contained" disabled={isBuilding || !variant} onClick={run}>
          {isBuilding ? <CircularProgress size={16} color="inherit" /> : result ? "Build again" : "Make the link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
